import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Clock, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { services } from "@/data/services";

const trustSignals = [
  { icon: BadgeCheck, label: "Service Disabled Veteran-Owned Small Business" },
  { icon: Award, label: "HIRE Vets Platinum Award" },
  { icon: Clock, label: "15+ Years of Service" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 } as const,
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0, 0, 0.2, 1] as const },
  }),
};

const Index = () => {
  return (
    <Layout>
      <SEO
        title="Integrated Defense & Intelligence Solutions"
        description="Robinson Consulting Group is a Service Disabled Veteran-Owned Small Business (SDVOSB) delivering mission-critical IT consulting, systems integration, and cybersecurity solutions for Defense and Intelligence agencies."
        path="/"
      />
      {/* Hero */}
      <section className="relative overflow-hidden gradient-navy">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(circle at 20% 50%, hsl(197 76% 55% / 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, hsl(93 52% 51% / 0.2) 0%, transparent 50%)",
          }} />
        </div>
        <div className="container relative py-24 md:py-36 lg:py-44">
          <div
            className="max-w-3xl"
          >
            <p className="mb-4 font-display text-sm font-semibold uppercase tracking-widest text-rcg-sky">
              Integrated Defense & Intelligence Solutions
            </p>
            <h1 className="font-display text-4xl font-extrabold leading-tight text-white md:text-5xl lg:text-6xl">
              From Vision to Execution for the{" "}
              <span className="text-gradient">Modern Warfighter</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">
              Robinson Consulting Group integrates people, technology, and mission systems to turn strategy into execution. We serve as the operational backbone for Defense and Intelligence organizations—driving initiatives from concept to completion with precision, accountability, and mission-first urgency.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button size="lg" asChild className="bg-rcg-sky hover:bg-rcg-blue text-white">
                <Link to="/services">Our Services</Link>
              </Button>
              <Button size="lg" asChild className="bg-rcg-green hover:bg-rcg-green/90 text-white">
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Pillars */}
      <section className="py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} custom={0} className="font-display text-sm font-semibold uppercase tracking-widest text-rcg-blue">
              What We Do
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="mt-2 font-display text-3xl font-bold md:text-4xl">
              Core Capabilities
            </motion.h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((service, i) => (
              <motion.div
                key={service.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                variants={fadeUp}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="group flex h-full flex-col rounded-xl border border-border bg-card p-6 transition-all hover:border-rcg-sky/40 hover:shadow-lg hover:shadow-rcg-sky/5"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-rcg-sky group-hover:text-white">
                    <service.icon size={24} />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{service.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {service.description}
                  </p>
                  <span className="mt-4 text-sm font-medium text-primary underline-offset-2 group-hover:underline">
                    Learn more →
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="border-y border-border bg-muted/50 py-16">
        <div className="container">
          <div className="grid gap-8 sm:grid-cols-3">
            {trustSignals.map((signal, i) => (
              <motion.div
                key={signal.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="flex items-center gap-4"
              >
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-rcg-green/10 text-rcg-green">
                  <signal.icon size={28} />
                </div>
                <span className="font-display text-base font-semibold">{signal.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={0}
            className="gradient-navy rounded-2xl p-10 md:p-16 text-center"
          >
            <h2 className="font-display text-3xl font-bold text-white md:text-4xl">
              Ready to Advance Your Mission?
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-white/85">
              Partner with a veteran-owned team that understands the unique demands of Defense and Intelligence operations.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button size="lg" asChild className="bg-rcg-sky hover:bg-rcg-blue text-white">
                <Link to="/contact">Contact Us</Link>
              </Button>
              <Button size="lg" asChild className="bg-rcg-green hover:bg-rcg-green/90 text-white">
                <Link to="/careers">View Careers</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
